import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  ReplaySubject,
  retryWhen,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs';

import { getBonusTimeLeft, getDaysLeft } from '@personal-area/common';
import {
  AccountAnalytics,
  AccountAnalyticsApiService,
  AccountAnalyticsService,
  AnalyticsPeriod,
} from '@personal-area/global/account-analytics';
import { Account } from '@personal-area/global/accounts/models';
import { UnsubscribeController } from '@personal-area/global/common';
import {
  DepositBonusFacade,
  DepositBonusTrackingService,
} from '@personal-area/global/deposit-bonus';
import { retryStrategy } from '@personal-area/global/http/services';
import { I18nService } from '@personal-area/global/i18n';

type State = 'loading' | 'noTrade' | 'success' | 'failed';

const ANALYTICS_FALLBACK: AccountAnalytics = {
  lots: 0,
  loss: 0,
  profit: 0,
  netProfit: 0,
  tradingVolume: 0,
  closedOrders: 0,
  netProfitFraction: 0,
  imageLink: '',
  lastUpdate: new Date().toISOString(),
};

@Component({
    selector: 'global-account-viewer-performance',
    templateUrl: './account-viewer-performance.component.html',
    styleUrls: ['./account-viewer-performance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountViewerPerformanceComponent
  extends UnsubscribeController
  implements OnChanges, OnInit
{
  private readonly _destroyRef = inject(DestroyRef);

  @Input() account!: Account;
  @Input()
  set depositBonusExpirationDate(value: string) {
    this.depositBonusExpirationDate$.next(value);
  }
  private _depositBonusFacade = inject(DepositBonusFacade);

  public depositBonusAvailability$ =
    this._depositBonusFacade.depositBonusAvailability$;
  public depositBonusExpirationDate$ = new ReplaySubject<string>(1);
  public bonusTimeLeft$ = this.depositBonusExpirationDate$.pipe(
    filter(Boolean),
    take(1),
    map(getBonusTimeLeft),
  );
  public daysLeft$ = this.depositBonusExpirationDate$.pipe(
    filter(Boolean),
    take(1),
    map(getDaysLeft),
  );

  public state$ = new BehaviorSubject<State>('loading');
  public periods: AnalyticsPeriod[] = this._analyticsService.buildPeriods();
  public periodControl = new FormControl<AnalyticsPeriod>(this.periods[0], {
    nonNullable: true,
  });
  public analytics = ANALYTICS_FALLBACK;

  constructor(
    private _analyticsApiService: AccountAnalyticsApiService,
    private _analyticsService: AccountAnalyticsService,
    private _i18nService: I18nService,
    private readonly _depositBonusTrackingService: DepositBonusTrackingService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this._initDepositBonusExpirationDateSubscriber();

    this.periodControl.valueChanges
      .pipe(
        throttleTime(1000),
        tap(() => this.state$.next('loading')),
        switchMap(({ from, to }: AnalyticsPeriod) =>
          this._analyticsApiService
            .fetchAnalytics(
              this.account.id,
              from.toISOString(),
              to.toISOString(),
            )
            .pipe(
              retryWhen(retryStrategy()),
              catchError(({ status }: HttpErrorResponse) => {
                if (status === 404) {
                  return of(ANALYTICS_FALLBACK);
                }

                this.state$.next('failed');
                return of(null);
              }),
            ),
        ),
        filter(Boolean),
        takeUntil(this._unsubscribe),
      )
      .subscribe((analytics) => {
        this.analytics = analytics;
        this.state$.next('success');
      });

    if (this.account.isHasTrades) {
      this.loadAnalytics();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.account.isHasTrades) {
      this.loadAnalytics();
    } else {
      this.state$.next('noTrade');
    }
  }

  public loadAnalytics(): void {
    this.periodControl.updateValueAndValidity();
  }

  public get language(): string {
    return this._i18nService.language;
  }

  public hasDatePassed(date: string): boolean {
    return new Date(date).getTime() < Date.now();
  }

  private _initDepositBonusExpirationDateSubscriber() {
    this.depositBonusExpirationDate$
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe((expiredDepositBonusViewed) => {
        if (this.account.credit) {
          this._depositBonusTrackingService.expiredDepositBonusViewed(expiredDepositBonusViewed);
        }
      });
  }
}
